import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Plus, Trash2, Landmark, Info, Pencil, BellRing, X, PartyPopper, Share2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { simulateDebts, paymentSplit, cardPaymentBreakdown, interestAlerts } from "../lib/finance";
import { fmt, fmt0, monthLabel, uid, today } from "../lib/format";
import { PageHeader, Section, Banner, Money, Empty, ChartTip } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { cn } from "../lib/utils";
import DebtCountdown, { ProgressRing, paidPct, MilestoneTimeline } from "../components/DebtCountdown";
import { ShareButton, ShareDialog, shareInfo } from "../components/ShareCard";
import MilestoneBurst from "../components/MilestoneBurst";

function DebtDialog({ debt, onClose, onPaidOff }) {
  const { update, recordDebtPayment } = useStore();
  const [f, setF] = useState(debt || { creditor: "", balance: "", rate: "", minPayment: "", dueDay: "" });
  const save = () => {
    if (!f.creditor || f.balance === "" || Number(f.balance) < 0 || (!debt && !(Number(f.balance) > 0))) return toast.error("Creditor and balance are required");
    const paidDown = debt && Number(debt.balance) > 0 && Number(f.balance) < Number(debt.balance);
    const data = { ...f, balance: paidDown ? Number(debt.balance) : Number(f.balance), rate: Number(f.rate || 0), minPayment: Number(f.minPayment || 0), dueDay: f.dueDay ? Number(f.dueDay) : "" };
    update(debt ? "Edit debt" : "Add debt", (d) => {
      if (debt) {
        const cur = d.debts.find((x) => x.id === debt.id);
        if (Number(cur.rate) !== data.rate) data.rateHistory = [...(cur.rateHistory || []), { date: today(), from: Number(cur.rate), to: data.rate }];
        Object.assign(cur, data);
      }
      else d.debts.push({ id: uid(), ...data, baseline: { date: today(), balance: data.balance } });
    });
    if (paidDown) {
      const res = recordDebtPayment(debt.id, { newBalance: Number(f.balance) });
      if (res) onPaidOff(res);
    }
    onClose();
  };
  const fields = [["creditor", "Creditor", "text"], ["balance", "Outstanding balance", "number"], ["rate", "Interest rate (APR %)", "number"], ["minPayment", "Minimum monthly payment", "number"], ["dueDay", "Payment due day (1–31)", "number"]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="debt-dialog">
        <DialogHeader><DialogTitle>{debt ? "Edit debt" : "Add a debt"}</DialogTitle><DialogDescription>Enter the figures from your latest statement.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          {fields.map(([k, l, t]) => <div key={k}><Label>{l}</Label><Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`debt-${k}-input`} /></div>)}
          <p className="text-xs text-muted-foreground">Use the figures on your latest loan or card statement. We never guess balances or rates from bank transactions.</p>
        </div>
        <DialogFooter><Button onClick={save} data-testid="debt-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CONFETTI = ["#0D9488", "#6366F1", "#F59E0B", "#F43F5E", "#10B981", "#3B82F6"];

function Celebration({ info, onClose, onShare }) {
  const pieces = Array.from({ length: 70 }, (_, i) => ({ left: (i * 37) % 100, delay: (i % 14) * 0.12, dur: 2.4 + (i % 5) * 0.35, color: CONFETTI[i % CONFETTI.length], rot: (i * 47) % 360, size: 6 + (i % 4) * 2 }));
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/50 backdrop-blur-sm px-4" onClick={onClose} data-testid="payoff-celebration">
      <style>{`@keyframes hb-fall { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1 } 100% { transform: translateY(105vh) rotate(720deg); opacity: .9 } }`}</style>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        {pieces.map((p, i) => <span key={i} className="absolute top-0 rounded-sm" style={{ left: `${p.left}%`, width: p.size, height: p.size * 1.6, background: p.color, transform: `rotate(${p.rot}deg)`, animation: `hb-fall ${p.dur}s ${p.delay}s cubic-bezier(.3,.6,.4,1) forwards` }} />)}
      </div>
      <div className="relative bg-card rounded-3xl shadow-2xl p-8 max-w-md w-full text-center fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="h-16 w-16 rounded-full bg-income-soft grid place-items-center mx-auto"><PartyPopper className="h-8 w-8 text-income" /></div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mt-5">Debt paid off</p>
        <h2 className="text-3xl font-extrabold mt-2" data-testid="payoff-title">{info.name} is gone!</h2>
        <p className="text-muted-foreground mt-3" data-testid="payoff-savings">
          {info.neverPaid ? "At the minimum payment this debt would never have been paid off — you broke the cycle." : <>Compared with paying only the minimum, you avoided about <b className="text-income text-lg">{fmt(info.interestSaved)}</b> in interest{info.monthsEarly ? <> and finished <b>{info.monthsEarly} month{info.monthsEarly === 1 ? "" : "s"} early</b></> : ""}.</>}
        </p>
        <p className="text-xs text-muted-foreground mt-2">Estimate based on the balance, rate and minimum you entered.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {info.id && <Button variant="outline" className="rounded-full" onClick={() => onShare(info.id)} data-testid="payoff-share-button"><Share2 className="h-4 w-4 mr-1" /> Share</Button>}
          <Button className="rounded-full px-8" onClick={onClose} data-testid="payoff-close-button">Celebrate & continue</Button>
        </div>
      </div>
    </div>
  );
}


function PaymentDialog({ debt, onClose, onPaidOff }) {
  const { recordDebtPayment } = useStore();
  const [mode, setMode] = useState("payment");
  const [v, setV] = useState("");
  const save = () => {
    const n = Number(v);
    if (v === "" || n < 0 || (mode === "payment" && n <= 0)) return toast.error("Enter an amount");
    const res = recordDebtPayment(debt.id, mode === "payment" ? { payment: n } : { newBalance: n });
    onClose();
    if (res) onPaidOff(res);
    else toast.success(`Updated ${debt.creditor}`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Update {debt.creditor}</DialogTitle><DialogDescription>Current balance {fmt(debt.balance)}. For the most accurate number, use the balance on your latest statement.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "payment" ? "default" : "outline"} onClick={() => setMode("payment")} data-testid="payment-mode-payment">Record a payment</Button>
          <Button size="sm" variant={mode === "balance" ? "default" : "outline"} onClick={() => setMode("balance")} data-testid="payment-mode-balance">Enter new balance</Button>
        </div>
        <div><Label>{mode === "payment" ? "Payment amount (reduces the balance by this amount)" : "New balance from statement"}</Label><Input type="number" value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid="payment-amount-input" /></div>
        <Button variant="link" className="px-0 justify-start" onClick={() => { setMode("balance"); setV("0"); }} data-testid="payment-paid-in-full">It's paid in full</Button>
        <DialogFooter><Button onClick={save} data-testid="payment-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaidOffList({ debts }) {
  const paid = debts.filter((d) => d.paidOff && Number(d.balance) === 0);
  if (!paid.length) return null;
  return (
    <Section title="Debt-free wins" subtitle="Debts you've paid off" className="mt-6" testid="paid-off-list">
      <ul className="divide-y text-sm">
        {paid.map((d) => (
          <li key={d.id} className="py-3" data-testid={`paid-off-${d.id}`}>
            <div className="flex flex-wrap justify-between gap-2">
              <span className="flex items-center gap-2 font-medium"><PartyPopper className="h-4 w-4 text-income" /> {d.creditor} <span className="text-muted-foreground font-normal">· paid off {monthLabel(d.paidOff.date.slice(0, 7))}</span></span>
              <span className="text-income">{d.paidOff.neverPaid || d.paidOff.interestSaved === null ? "Minimum payments would never have cleared it" : `≈ ${fmt(d.paidOff.interestSaved)} interest avoided${d.paidOff.monthsEarly ? ` · ${d.paidOff.monthsEarly} months early` : ""}`}</span>
            </div>
            <div className="flex flex-wrap items-end gap-3 mt-3"><MilestoneTimeline debt={d} className="max-w-md flex-1" /><ShareButton debt={d} /></div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function InterestAlerts({ compact }) {
  const { state, update } = useStore();
  const alerts = interestAlerts(state);
  if (!alerts.length) return null;
  const list = compact ? alerts.filter((a) => a.severity === "high").slice(0, 2) : alerts;
  if (!list.length) return null;
  return (
    <div className={compact ? "space-y-3" : "space-y-3 mb-6"} data-testid={compact ? "dashboard-interest-alerts" : "interest-alerts"}>
      {!compact && <p className="font-display font-bold flex items-center gap-2"><BellRing className="h-5 w-5 text-warning" /> Interest alerts</p>}
      {list.map((a) => (
        <Banner key={a.id} tone={a.severity === "high" ? "danger" : "warning"} icon={BellRing} testid={`interest-alert-${a.id}`} action={<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => update("Dismiss alert", (d) => (d.dismissedAlerts = [...(d.dismissedAlerts || []), a.id]))} title="Dismiss" data-testid={`dismiss-interest-alert-${a.id}`}><X className="h-4 w-4" /></Button>}>
          <b>{a.title}.</b> {a.text}
        </Banner>
      ))}
    </div>
  );
}

function PaymentBreakdown({ debts, sim, extra }) {
  const chart = sim.schedule.slice(1, 25).map((s) => ({ month: `M${s.month}`, Interest: s.interest, Principal: s.principal }));
  const first12 = sim.schedule.slice(1, 13).reduce((a, s) => ({ i: a.i + s.interest, p: a.p + s.principal }), { i: 0, p: 0 });
  return (
    <Section title="Where your payments go" subtitle="How much of each payment is interest versus principal (the amount that actually lowers what you owe). Based on the balances and rates you entered." testid="payment-breakdown">
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[600px]" data-testid="payment-split-table">
          <thead className="text-xs uppercase text-muted-foreground border-b"><tr><th className="p-2 text-left">Debt</th><th className="p-2 text-right">Next minimum payment</th><th className="p-2 text-right">Interest</th><th className="p-2 text-right">Principal</th><th className="p-2 text-left w-1/3">Split</th></tr></thead>
          <tbody>
            {debts.map((d) => {
              const sp = paymentSplit(d);
              return (
                <tr key={d.id} className="border-b last:border-0" data-testid={`payment-split-${d.id}`}>
                  <td className="p-2 font-medium">{d.creditor}</td>
                  <td className="p-2 text-right"><Money value={sp.payment} /></td>
                  <td className="p-2 text-right text-expense"><Money value={sp.interest} /></td>
                  <td className="p-2 text-right text-income"><Money value={sp.principal} /></td>
                  <td className="p-2">
                    <div className="flex h-2.5 rounded-full overflow-hidden bg-secondary"><div style={{ width: `${sp.pctInterest}%` }} className="bg-[hsl(var(--expense))]" /><div style={{ width: `${100 - sp.pctInterest}%` }} className="bg-[hsl(var(--income))]" /></div>
                    <p className="text-[11px] text-muted-foreground mt-1">{sp.coversInterest ? `${sp.pctInterest}% goes to interest` : "Payment doesn't cover the monthly interest — the balance will grow"}</p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!sim.neverPaid && (
        <>
          <p className="text-sm mt-5" data-testid="first-year-split">Over the next 12 months (avalanche{extra ? ` with ${fmt0(extra)} extra` : ""}), you'd pay <b className="text-expense">{fmt(first12.i)}</b> in interest and <b className="text-income">{fmt(first12.p)}</b> toward principal.</p>
          <div className="h-64 mt-3">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Principal" stackId="a" fill="hsl(var(--income))" />
                <Bar dataKey="Interest" stackId="a" fill="hsl(var(--expense))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Section>
  );
}

function CardStatementSplit() {
  const { state } = useStore();
  const cards = cardPaymentBreakdown(state).filter((c) => c.rows.length);
  if (!cards.length) return null;
  return (
    <Section className="mt-6" title="Credit card payments from your statements" subtitle="Uses only what's on your imported card statements: payments you made, interest and fees the card charged. Principal = payments − interest − fees." testid="card-statement-split">
      {cards.map((c) => (
        <div key={c.account.id} className="mb-6 last:mb-0" data-testid={`card-split-${c.account.id}`}>
          <p className="font-semibold">{c.account.name}</p>
          <p className="text-sm text-muted-foreground mb-2">
            Total paid {fmt(c.totals.payments)} · interest charged <span className="text-expense">{fmt(c.totals.interest)}</span> · fees {fmt(c.totals.fees)} · toward principal <span className="text-income">{fmt(c.totals.principal)}</span>
            {c.totals.payments > 0 && c.totals.interest > 0 && <> — {((c.totals.interest / c.totals.payments) * 100).toFixed(1)}% of your payments covered interest.</>}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="text-xs uppercase text-muted-foreground border-b"><tr><th className="p-2 text-left">Month</th><th className="p-2 text-right">New purchases</th><th className="p-2 text-right">Payments</th><th className="p-2 text-right">Interest</th><th className="p-2 text-right">Fees</th><th className="p-2 text-right">Principal</th></tr></thead>
              <tbody>
                {c.rows.map((r) => (
                  <tr key={r.month} className="border-b last:border-0"><td className="p-2">{monthLabel(r.month)}</td><td className="p-2 text-right"><Money value={r.purchases} /></td><td className="p-2 text-right"><Money value={r.payments} /></td><td className="p-2 text-right text-expense"><Money value={r.interest} /></td><td className="p-2 text-right"><Money value={r.fees} /></td><td className="p-2 text-right text-income"><Money value={r.principal} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {c.totals.interest === 0 && <p className="text-xs text-muted-foreground mt-2">No interest charges found on these statements — your payments went entirely toward the balance.</p>}
        </div>
      ))}
    </Section>
  );
}

export default function Debt() {
  const { state, update } = useStore();
  const [extra, setExtra] = useState(100);
  const [dialog, setDialog] = useState(null);
  const debts = state.debts.filter((d) => Number(d.balance) > 0);
  const [celebrate, setCelebrate] = useState(null);
  const [paying, setPaying] = useState(null);
  const [burst, setBurst] = useState(null);
  const [shareDebt, setShareDebt] = useState(null);
  const endBurst = useCallback(() => setBurst(null), []);
  const onResult = (res) => {
    if (!res.milestone) return setCelebrate(res);
    setBurst(res);
    toast.success(`${res.milestone}% of ${res.name} paid off — ${fmt(res.balance)} to go. Keep it up!`, { action: { label: "Share", onClick: () => { setBurst(null); setShareDebt(res.id); } } });
  };
  const sims = useMemo(() => ({
    minimum: simulateDebts(debts, 0, "minimum"),
    snowball: simulateDebts(debts, extra, "snowball"),
    avalanche: simulateDebts(debts, extra, "avalanche"),
    avalancheNoExtra: simulateDebts(debts, 0, "avalanche"),
  }), [debts, extra]);
  const total = debts.reduce((s, d) => s + Number(d.balance), 0);
  const totalMin = debts.reduce((s, d) => s + Number(d.minPayment || 0), 0);
  const chart = [];
  const len = Math.max(sims.minimum.schedule.length, sims.snowball.schedule.length);
  for (let i = 0; i < Math.min(len, 361); i += Math.max(1, Math.floor(len / 60))) chart.push({ month: `M${i}`, "Minimums only": sims.minimum.schedule[i]?.balance ?? 0, Snowball: sims.snowball.schedule[i]?.balance ?? 0, Avalanche: sims.avalanche.schedule[i]?.balance ?? 0 });
  const best = sims.avalanche.totalInterest <= sims.snowball.totalInterest ? "avalanche" : "snowball";
  const Strategy = ({ id, title, desc, sim }) => (
    <div className={cn("rounded-xl border p-4", id === best && "border-primary ring-1 ring-primary")} data-testid={`strategy-${id}`}>
      <p className="font-display font-bold">{title} {id === best && <span className="text-xs text-primary font-semibold ml-1">Lowest interest</span>}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between"><dt>Debt-free</dt><dd className="num font-semibold" data-testid={`strategy-${id}-date`}>{sim.neverPaid ? "Never at this payment" : monthLabel(sim.payoffMonth)}</dd></div>
        <div className="flex justify-between"><dt>Months</dt><dd className="num">{sim.months ?? "—"}</dd></div>
        <div className="flex justify-between"><dt>Total interest</dt><dd className="num text-expense" data-testid={`strategy-${id}-interest`}>{sim.neverPaid ? "—" : fmt(sim.totalInterest)}</dd></div>
        {id !== "minimum" && !sims.minimum.neverPaid && !sim.neverPaid && <div className="flex justify-between"><dt>Interest saved</dt><dd className="num text-income">{fmt(sims.minimum.totalInterest - sim.totalInterest)}</dd></div>}
      </dl>
    </div>
  );
  return (
    <div data-testid="debt-page">
      <PageHeader title="Debt" subtitle="See when you'll be debt-free and how extra payments speed it up.">
        <Button className="rounded-full" onClick={() => setDialog({})} data-testid="add-debt-button"><Plus className="h-4 w-4 mr-1" /> Add debt</Button>
      </PageHeader>
      <InterestAlerts />
      {!debts.length ? (
        <Section><Empty icon={Landmark} title="No debts entered" text="Add credit cards, car loans, student loans or personal loans with the balance and rate from your statements." testid="debt-empty"><Button onClick={() => setDialog({})}>Add your first debt</Button></Empty></Section>
      ) : (
        <div className="space-y-6">
          <Banner icon={Info}>Balances and interest rates come only from what you enter here. Estimates assume a fixed rate and no new charges.</Banner>
          <DebtCountdown debts={debts} strategySim={sims.avalanche} />
          <Section title="Your debts" subtitle={`${fmt(total)} total · ${fmt(totalMin)}/month in minimum payments`} testid="debt-list">
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="text-xs uppercase text-muted-foreground border-b"><tr><th className="p-2 text-left">Creditor</th><th className="p-2 text-right">Balance</th><th className="p-2 text-right">APR</th><th className="p-2 text-right">Minimum</th><th className="p-2 text-right">Due day</th><th className="p-2 text-right">Payoff (minimums)</th><th className="p-2 text-right">Interest (minimums)</th><th className="p-2"></th></tr></thead>
                <tbody>
                  {debts.map((d) => {
                    const alone = simulateDebts([d], 0, "minimum");
                    return (
                      <tr key={d.id} className="border-b last:border-0" data-testid={`debt-row-${d.id}`}>
                        <td className="p-2 font-medium"><span className="flex items-center gap-2"><ProgressRing pct={paidPct(d)} size={30} stroke={4} testid={`debt-row-ring-${d.id}`} /><span>{d.creditor}</span></span></td>
                        <td className="p-2 text-right"><Money value={d.balance} /></td>
                        <td className="p-2 text-right num">{d.rate}%</td>
                        <td className="p-2 text-right"><Money value={d.minPayment} /></td>
                        <td className="p-2 text-right num">{d.dueDay || "—"}</td>
                        <td className="p-2 text-right">{alone.neverPaid ? <span className="text-expense text-xs">Payment too low to cover interest</span> : monthLabel(alone.payoffMonth)}</td>
                        <td className="p-2 text-right">{alone.neverPaid ? "—" : <Money value={alone.totalInterest} />}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button variant="outline" size="sm" className="mr-1" onClick={() => setPaying(d)} data-testid={`record-payment-${d.id}`}>Pay</Button>
                          <Button variant="ghost" size="icon" onClick={() => setDialog(d)} data-testid={`edit-debt-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => update("Delete debt", (x) => (x.debts = x.debts.filter((y) => y.id !== d.id)))} data-testid={`delete-debt-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
          <Section title="Extra monthly payment" subtitle="Added on top of your minimums. When a debt is paid off, its minimum rolls to the next one." testid="extra-payment-section">
            <div className="flex items-center gap-4">
              <Slider value={[extra]} min={0} max={2000} step={25} onValueChange={(v) => setExtra(v[0])} className="flex-1" data-testid="extra-payment-slider" />
              <Input type="number" value={extra} onChange={(e) => setExtra(Math.max(0, Number(e.target.value) || 0))} className="w-28 num" data-testid="extra-payment-input" />
            </div>
            {!sims.avalancheNoExtra.neverPaid && !sims.avalanche.neverPaid && extra > 0 && (
              <p className="text-sm mt-3" data-testid="extra-impact">Paying an extra <b>{fmt0(extra)}</b>/month saves <b className="text-income">{fmt(sims.avalancheNoExtra.totalInterest - sims.avalanche.totalInterest)}</b> in interest and makes you debt-free <b>{sims.avalancheNoExtra.months - sims.avalanche.months} months sooner</b> (avalanche method).</p>
            )}
            <div className="grid md:grid-cols-3 gap-3 mt-5">
              <Strategy id="minimum" title="Minimums only" desc="Pay just the minimum on each debt." sim={sims.minimum} />
              <Strategy id="snowball" title="Debt snowball" desc="Extra goes to the smallest balance first — quick wins." sim={sims.snowball} />
              <Strategy id="avalanche" title="Debt avalanche" desc="Extra goes to the highest interest rate first — least interest." sim={sims.avalanche} />
            </div>
          </Section>
          <Section title="Payoff order" testid="payoff-order">
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              {["snowball", "avalanche"].map((k) => (
                <div key={k}>
                  <p className="font-semibold capitalize mb-2">{k}</p>
                  <ol className="space-y-1.5">
                    {[...sims[k].perDebt].sort((a, b) => (a.months ?? 9999) - (b.months ?? 9999)).map((p, i) => (
                      <li key={p.id} className="flex justify-between"><span>{i + 1}. {p.name}</span><span className="num text-muted-foreground">{p.payoffMonth ? monthLabel(p.payoffMonth) : "—"}</span></li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </Section>
          <PaymentBreakdown debts={debts} sim={sims.avalanche} extra={extra} />
          <Section title="Total balance over time" testid="debt-chart">
            <div className="h-72">
              <ResponsiveContainer>
                <LineChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line dataKey="Minimums only" stroke="#94A3B8" dot={false} strokeWidth={2} />
                  <Line dataKey="Snowball" stroke="#6366F1" dot={false} strokeWidth={2} />
                  <Line dataKey="Avalanche" stroke="#0D9488" dot={false} strokeWidth={2.5} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>
      )}
      <CardStatementSplit />
      <PaidOffList debts={state.debts} />
      {dialog && <DebtDialog debt={dialog.id ? dialog : null} onClose={() => setDialog(null)} onPaidOff={onResult} />}
      {paying && <PaymentDialog debt={paying} onClose={() => setPaying(null)} onPaidOff={onResult} />}
      {burst && <MilestoneBurst info={burst} onDone={endBurst} />}
      {shareDebt && state.debts.find((d) => d.id === shareDebt) && <ShareDialog info={shareInfo(state.debts.find((d) => d.id === shareDebt))} onClose={() => setShareDebt(null)} />}
      {celebrate && <Celebration info={celebrate} onClose={() => setCelebrate(null)} onShare={(id) => { setCelebrate(null); setShareDebt(id); }} />}
    </div>
  );
}
